/**
 * Infinite Scroll for Collection Pages
 * Automatically loads more products as user scrolls down
 */
(function() {
  'use strict';

  // Upper bound on consecutive all-filtered-out pages to skip in one pass, so a
  // long run of imageless products can't turn into an unbounded fetch loop.
  const MAX_EMPTY_PAGE_CHAIN = 10;

  // When a pass ends without appending anything (fetch failed, or the chain cap
  // was hit) the sentinel's intersection state is unchanged, so the observer
  // never fires again by itself. Re-arm it a bounded number of times with a
  // backoff instead of hammering the server forever.
  const MAX_REARM_ATTEMPTS = 3;
  const REARM_DELAY_MS = 1000;
  const REQUEST_TIMEOUT_MS = 15000;
  // Start before a fast swipe reaches the footer, including on taller phones.
  const LOAD_AHEAD_PX = Math.max(800, window.innerHeight * 2);

  class InfiniteScroll {
    constructor() {
      this.grid = document.getElementById('product-grid');
      this.pagination = document.getElementById('InfiniteScrollPagination');
      this.loader = document.getElementById('InfiniteScrollLoader');

      if (!this.grid || !this.pagination) {
        return;
      }

      this.nextUrl = this.pagination.dataset.nextUrl;
      this.isLoading = false;
      this.rearmAttempts = 0;
      this.destroyed = false;

      this.init();
    }

    init() {
      if (!this.nextUrl) {
        if (this.loader) this.loader.classList.add('is-complete');
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => this.handleIntersect(entries),
        { rootMargin: `${LOAD_AHEAD_PX}px 0px`, threshold: 0 }
      );

      this.observer.observe(this.pagination);
    }

    handleIntersect(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoading && this.nextUrl) {
          this.loadMore();
        }
      });
    }

    async loadMore() {
      if (this.destroyed || this.isLoading || !this.nextUrl) return;
      this.isLoading = true;
      if (this.loader) this.loader.classList.add('is-loading');

      // A page can now render fewer than products_per_page cards, or none at
      // all, when products are filtered out server-side (no featured media).
      // Too few cards may not push the sentinel past the observer's margin, and
      // the observer only fires on a CHANGE of intersection state — so keep
      // pulling until the sentinel is actually covered or pagination runs out.
      let guard = 0;
      let failed = false;
      while (this.nextUrl && guard < MAX_EMPTY_PAGE_CHAIN && this.grid.isConnected && !this.destroyed) {
        guard++;
        const appended = await this.fetchNextPage();
        if (appended === null) { failed = true; break; }
        if (appended > 0 && !this.sentinelInView()) break;
      }

      this.isLoading = false;
      if (this.loader) this.loader.classList.remove('is-loading');

      if (!this.nextUrl || !this.grid.isConnected || this.destroyed) return;

      if (failed || this.sentinelInView()) {
        // The request failed, or we stopped with the sentinel still in view
        // (chain cap hit). Either way nothing will re-trigger the observer on
        // its own, so schedule a retry rather than dead-ending the grid.
        this.rearm();
      } else {
        this.rearmAttempts = 0;
      }
    }

    /** Is the pagination sentinel within the observer's load-ahead margin? */
    sentinelInView() {
      const rect = this.pagination.getBoundingClientRect();
      return rect.top <= (window.innerHeight || document.documentElement.clientHeight) + LOAD_AHEAD_PX;
    }

    /**
     * Ask the observer to re-evaluate the sentinel after a backoff, so a failed
     * or fully-filtered pass can retry. Bounded — gives up rather than looping.
     */
    rearm() {
      if (this.rearmAttempts >= MAX_REARM_ATTEMPTS) {
        // Out of retries — stop the spinner rather than letting it turn forever.
        if (this.loader) this.loader.classList.add('is-complete');
        return;
      }
      this.rearmAttempts++;
      clearTimeout(this.rearmTimer);
      this.rearmTimer = setTimeout(() => {
        if (!this.nextUrl || !this.grid.isConnected || this.destroyed) return;
        if (this.isLoading) {
          // A scroll-triggered load beat us to it; retry without burning the
          // attempt, otherwise flaky timing permanently disables the retry.
          this.rearmAttempts--;
          this.rearm();
          return;
        }
        this.observer.unobserve(this.pagination);
        this.observer.observe(this.pagination);
      }, REARM_DELAY_MS * this.rearmAttempts);
    }

    /**
     * Fetch this.nextUrl, append its cards, advance pagination state.
     * Returns the number of cards appended, or null if the request failed.
     */
    async fetchNextPage() {
      const controller = new AbortController();
      this.request = controller;
      const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        // Section rendering skips the header, footer and platform scripts on
        // every page request. Preserve search, sort and Shopify filter params.
        const url = new URL(this.nextUrl, window.location.href);
        if (this.grid.dataset.id) url.searchParams.set('section_id', this.grid.dataset.id);
        const response = await fetch(url.toString(), { signal: controller.signal });
        if (!response.ok) throw new Error(response.status);
        const html = await response.text();
        if (this.destroyed || !this.grid.isConnected) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (!doc.getElementById('product-grid')) throw new Error('Missing product grid');

        const newProducts = doc.querySelectorAll('#product-grid > li');
        const newPagination = doc.getElementById('InfiniteScrollPagination');

        const fragment = document.createDocumentFragment();
        newProducts.forEach(product => {
          fragment.appendChild(product.cloneNode(true));
        });
        this.grid.appendChild(fragment);

        if (newPagination && newPagination.dataset.nextUrl) {
          this.nextUrl = newPagination.dataset.nextUrl;
          this.pagination.dataset.nextUrl = this.nextUrl;
        } else {
          this.nextUrl = null;
          if (this.loader) this.loader.classList.add('is-complete');
          this.observer.disconnect();
        }

        return newProducts.length;
      } catch (error) {
        return null;
      } finally {
        clearTimeout(timeout);
        if (this.request === controller) this.request = null;
      }
    }

    destroy() {
      this.destroyed = true;
      clearTimeout(this.rearmTimer);
      if (this.request) this.request.abort();
      if (this.observer) this.observer.disconnect();
    }
  }

  let activeScroll = null;
  function refresh() {
    const grid = document.getElementById('product-grid');
    const pagination = document.getElementById('InfiniteScrollPagination');
    if (activeScroll && activeScroll.grid === grid && activeScroll.pagination === pagination) return;
    if (activeScroll) activeScroll.destroy();
    activeScroll = grid && pagination ? new InfiniteScroll() : null;
  }

  // Initialize when DOM is ready
  function init() {
    refresh();

    // Re-init after AJAX filter changes (facets.js replaces ProductGridContainer innerHTML)
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      new MutationObserver(refresh).observe(gridContainer, { childList: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize after Shopify section reload (for theme editor)
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('#product-grid')) {
      refresh();
    }
  });

})();
